import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Check, Building2, Compass, BookOpen } from "lucide-react";
import { detectOrgSlug, fetchCobrand } from "../lib/api";

/**
 * Your college. A calm page that says who set this portal up and what
 * they hope you do with it. Reads entirely from the org's own record,
 * which an institution admin can edit. No individual data ever lands here.
 */
export default function CollegeScreen({ product }) {
  const base = product === "grow" ? "/grow" : "";
  const [college, setCollege] = useState(undefined);

  useEffect(() => {
    const slug = detectOrgSlug();
    if (!slug) { setCollege(null); return; }
    fetchCobrand(slug).then((d) => setCollege(d || null)).catch(() => setCollege(null));
  }, []);

  if (college === undefined) {
    return (
      <div className="max-w-3xl mx-auto px-5 pt-16" data-testid="college-loading">
        <div className="k-quiet">Loading your college...</div>
      </div>
    );
  }

  if (college === null) {
    return (
      <div className="max-w-2xl mx-auto px-5 pt-16 text-center" data-testid="college-empty">
        <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal) / 0.12)", color: "hsl(var(--klareet-teal))" }}>
          <Building2 size={26} />
        </div>
        <h1 className="klareet-serif mb-2" style={{ fontSize: "1.6rem" }}>No college linked yet.</h1>
        <p className="k-quiet mb-5">You are not on a college seat, so there is nothing to show here. Your read is still yours and private either way.</p>
        <Link to={`${base}/home`} className="k-btn k-btn-outline" data-testid="college-back-home">Back to your dashboard</Link>
      </div>
    );
  }

  const name = college.display_name || "your college";
  const initial = name.slice(0, 1).toUpperCase();

  return (
    <div className="max-w-3xl mx-auto px-5 pt-8 pb-24" data-testid="college-page">
      <div className="k-label mb-2 flex items-center gap-2"><GraduationCap size={13} /> your college</div>

      {/* Header */}
      <div className="k-card mb-6" data-testid="college-header">
        <div className="flex items-center gap-4">
          {college.logo_url ? (
            <img src={college.logo_url} alt={name} className="h-14 w-14 object-contain rounded-xl" data-testid="college-header-logo" />
          ) : (
            <div className="h-14 w-14 rounded-xl flex items-center justify-center text-2xl font-semibold shrink-0" style={{ background: "hsl(var(--klareet-teal))", color: "white" }} data-testid="college-header-logo">
              {initial}
            </div>
          )}
          <div className="min-w-0">
            <h1 className="klareet-serif" style={{ fontSize: "clamp(1.6rem, 3.5vw, 2.2rem)", lineHeight: 1.1, color: "hsl(var(--klareet-ink))" }} data-testid="college-name">
              {name}
            </h1>
            <div className="k-quiet !text-sm mt-1">Your portal, set up by your college</div>
          </div>
        </div>
      </div>

      {/* About */}
      <section className="k-card mb-6" data-testid="college-about">
        <div className="k-label mb-2">about {name.toLowerCase() === "your college" ? "it" : name}</div>
        <p className="klareet-serif" style={{ fontSize: "1.05rem", lineHeight: 1.7, color: "hsl(var(--klareet-ink))" }} data-testid="college-about-text">
          {college.about}
        </p>
      </section>

      {/* Key facts */}
      {Array.isArray(college.key_facts) && college.key_facts.length > 0 && (
        <section className="mb-6" data-testid="college-facts">
          <div className="k-label mb-3">the quick facts</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="college-facts-grid">
            {college.key_facts.map((f, i) => (
              <div key={i} className="k-card !p-4" data-testid={`college-fact-${i}`}>
                <div className="klareet-serif" style={{ fontSize: "1.35rem", lineHeight: 1.1, color: "hsl(var(--klareet-ink))" }}>{f.value}</div>
                <div className="k-quiet !text-xs mt-1">{f.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Programs */}
      {Array.isArray(college.programs) && college.programs.length > 0 && (
        <section className="k-card mb-6" data-testid="college-programs">
          <div className="k-label mb-1 flex items-center gap-2"><BookOpen size={13} /> what you can study here</div>
          <p className="k-quiet !text-sm mb-4">A few of the paths on offer. Each one is a different way in.</p>
          <ul className="space-y-3" data-testid="college-programs-list">
            {college.programs.map((p, i) => (
              <li key={i} className="rounded-xl p-4" style={{ border: "1px solid hsl(var(--klareet-line-soft))", background: "hsl(var(--klareet-canvas-2))" }} data-testid={`college-program-${i}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{p.name}</div>
                  {p.level && (
                    <span className="k-chip !text-[11px]" style={{ color: "hsl(var(--klareet-teal))", background: "hsl(var(--klareet-teal) / 0.12)", border: "none" }}>{p.level}</span>
                  )}
                </div>
                {p.blurb && <p className="text-sm mt-1.5" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.55 }}>{p.blurb}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Expectations */}
      <section className="k-card mb-6" data-testid="college-expects">
        <div className="k-label mb-1">what {name} hopes you do here</div>
        <p className="k-quiet !text-sm mb-4">Not rules. Just the way to get the most out of this.</p>
        <ul className="space-y-3" data-testid="college-expects-list">
          {(college.expects || []).map((line, i) => (
            <li key={i} className="flex items-start gap-3" data-testid={`college-expect-${i}`}>
              <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "hsl(var(--klareet-teal) / 0.15)", color: "hsl(var(--klareet-teal))" }}>
                <Check size={13} />
              </span>
              <span style={{ color: "hsl(var(--klareet-ink))", lineHeight: 1.55 }}>{line}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="flex flex-wrap gap-3">
        <Link to={`${base}/home`} className="k-btn k-btn-primary" data-testid="college-cta-dashboard">
          <Compass size={14} /> Go to your dashboard
        </Link>
        <Link to={`${base}/roadmap`} className="k-btn k-btn-outline" data-testid="college-cta-roadmap">
          See your roadmap
        </Link>
      </div>
    </div>
  );
}
